import React, { useEffect, useRef, useState } from 'react';
import Icon from 'src/components/common/Icon';
import {
    PortfolioProperty,
    PortfolioSlot,
} from 'src/redux/user_portfolio/user_portfolio.types';
import {
    daysUntilExpiration,
    formatExpirationDate,
    isExpiringSoon,
    partitionSlots,
    propertyTitle,
    SlotCategory,
    summarizeSlots,
} from './assignmentsHelpers';
import { PROPERTY_DND_TYPE } from './PropertiesPanel';
import Pagination from './Pagination';
import {
    AvailableCard,
    CategoryCard,
    CategoryHeader,
    CategoryHeading,
    CategoryIcon,
    CategoryTitle,
    ClearLink,
    EmptyGroup,
    GroupLabel,
    KebabButton,
    MenuItem,
    MenuPopover,
    MenuWrapper,
    OccupiedHeaderLeft,
    ProgressFill,
    ProgressTrack,
    ReplaceBadge,
    SelectAllRow,
    SlotCard,
    SlotGrid,
    SlotSelect,
} from './VisibilityAssignment.styles';

/** Lee los IDs de propiedad de un evento de arrastre (payload separado por comas). */
const readDraggedPropertyIds = (e: React.DragEvent): number[] => {
    const raw = e.dataTransfer.getData(PROPERTY_DND_TYPE) || e.dataTransfer.getData('text/plain');
    return raw
        .split(',')
        .map((part) => Number(part.trim()))
        .filter((id) => !Number.isNaN(id));
};

const CATEGORY_META: Record<SlotCategory, { title: string; icon: string }> = {
    prime: { title: 'Prime', icon: 'pcom-icon-bolt' },
    destacado: { title: 'Destacados', icon: 'pcom-icon-rate-empty' },
};

/** Columnas de las grillas de slots. */
const SLOT_COLUMNS = 4;
/** Tamaño de página por defecto si el contenedor no pasa uno explícito. */
const DEFAULT_SECTION_PAGE_SIZE = SLOT_COLUMNS * 4;

interface SlotCategorySectionProps {
    category: SlotCategory;
    slots: PortfolioSlot[];
    /** Elementos por página (client-side) para ocupados y disponibles. */
    pageSize?: number;
    /** Opciones del selector "Slots por página" mostrado junto al paginador. */
    pageSizeOptions?: number[];
    /** Cambia el tamaño de página (compartido por ambas categorías). */
    onPageSizeChange?: (size: number) => void;
    /** Propiedades del anunciante indexadas por id, para mostrar nombre/ID en ocupados. */
    propertyById: Record<number, PortfolioProperty>;
    selectedOccupiedIds: number[];
    canAssignByClick: boolean;
    /** Nº de propiedades libres seleccionadas (para "armar" los slots disponibles). */
    selectedCount: number;
    onToggleOccupied: (slotId: number) => void;
    onSelectAllOccupied: (occupiedIds: number[], checked: boolean) => void;
    onLiberateSingle: (slot: PortfolioSlot) => void;
    /** Limpia la selección de slots ocupados (de todas las categorías). */
    onClearOccupied: () => void;
    onAssignClick: (slot: PortfolioSlot) => void;
    onDropProperty: (slot: PortfolioSlot, propertyIds: number[]) => void;
    /** Reemplaza la propiedad de un slot ocupado arrastrando una propiedad libre. */
    onReplaceProperty: (slot: PortfolioSlot, propertyIds: number[], slotNumber: number) => void;
}

interface OccupiedSlotCardProps {
    slot: PortfolioSlot;
    index: number;
    category: SlotCategory;
    property?: PortfolioProperty;
    selected: boolean;
    onToggle: (slotId: number) => void;
    onLiberate: (slot: PortfolioSlot) => void;
    onReplaceProperty: (slot: PortfolioSlot, propertyIds: number[], slotNumber: number) => void;
}

const OccupiedSlotCardView = ({
    slot,
    index,
    category,
    property,
    selected,
    onToggle,
    onLiberate,
    onReplaceProperty,
}: OccupiedSlotCardProps) => {
    const [menuOpen, setMenuOpen] = useState(false);
    const [replaceActive, setReplaceActive] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const warn = isExpiringSoon(slot);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const handler = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [menuOpen]);

    return (
        <SlotCard
            $category={category}
            $selected={selected}
            $replaceActive={replaceActive}
            data-id={`slot-occupied-${slot.id}`}
            onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (!replaceActive) setReplaceActive(true);
            }}
            onDragEnter={(e) => {
                e.preventDefault();
                setReplaceActive(true);
            }}
            onDragLeave={(e) => {
                // Ignora el dragleave hacia hijos internos del propio slot.
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setReplaceActive(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                setReplaceActive(false);
                const propertyIds = readDraggedPropertyIds(e);
                if (propertyIds.length > 0) onReplaceProperty(slot, propertyIds, index + 1);
            }}
        >
            {replaceActive && (
                <ReplaceBadge $category={category} data-id={`slot-replace-badge-${slot.id}`}>
                    <Icon icon="pcom-icon-refresh" size={14} />
                    Reemplazar propiedad
                </ReplaceBadge>
            )}
            <div className="slot-top">
                <SlotSelect>
                    <input
                        type="checkbox"
                        data-id={`slot-checkbox-${slot.id}`}
                        checked={selected}
                        onChange={() => onToggle(slot.id)}
                        aria-label={`Seleccionar slot ${slot.id}`}
                    />
                    <span className="slot-index">{String(index + 1).padStart(2, '0')}</span>
                </SlotSelect>
                <MenuWrapper ref={wrapperRef}>
                    <KebabButton
                        type="button"
                        data-id={`slot-menu-${slot.id}`}
                        aria-label={`Acciones del slot ${slot.id}`}
                        onClick={() => setMenuOpen((prev) => !prev)}
                    >
                        <Icon icon="pcom-icon-more-vertical" size={16} />
                    </KebabButton>
                    {menuOpen && (
                        <MenuPopover role="menu">
                            <MenuItem
                                type="button"
                                data-id={`slot-liberate-${slot.id}`}
                                onClick={() => {
                                    setMenuOpen(false);
                                    onLiberate(slot);
                                }}
                            >
                                <Icon icon="pcom-icon-delete" size={16} />
                                Liberar slot
                            </MenuItem>
                        </MenuPopover>
                    )}
                </MenuWrapper>
            </div>
            <div className="slot-property">
                <span className="dot" />
                <span className="slot-name">
                    {property ? propertyTitle(property) : `Propiedad ${slot.property_id ?? ''}`}
                </span>
            </div>
            <div className="slot-meta">
                ID {slot.property_id}
                {property?.address_array?.city ? ` · ${property.address_array.city}` : ''}
            </div>
            <div className="slot-meta">Slot {slot.id}</div>
            <div className="slot-status">
                <span className={`dot ${warn ? 'warn' : ''}`} />
                {warn ? 'Por vencer · ' : 'Vence · '}
                {slot.date_end || 'Sin fecha'}
            </div>
        </SlotCard>
    );
};

interface AvailableSlotCardProps {
    slot: PortfolioSlot;
    index: number;
    category: SlotCategory;
    /** Cantidad de propiedades libres seleccionadas; si > 0, el slot queda "armado". */
    selectedCount: number;
    onAssignClick: (slot: PortfolioSlot) => void;
    onDropProperty: (slot: PortfolioSlot, propertyIds: number[]) => void;
}

const AvailableSlotCardView = ({
    slot,
    index,
    category,
    selectedCount,
    onAssignClick,
    onDropProperty,
}: AvailableSlotCardProps) => {
    const [active, setActive] = useState(false);
    const days = daysUntilExpiration(slot);
    const niceDate = formatExpirationDate(slot.date_end);
    const hasSelection = selectedCount > 0;

    return (
        <AvailableCard
            type="button"
            $category={category}
            $active={active}
            data-id={`slot-available-${slot.id}`}
            onClick={() => onAssignClick(slot)}
            onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (!active) setActive(true);
            }}
            onDragEnter={(e) => {
                e.preventDefault();
                setActive(true);
            }}
            onDragLeave={() => setActive(false)}
            onDrop={(e) => {
                e.preventDefault();
                setActive(false);
                const propertyIds = readDraggedPropertyIds(e);
                if (propertyIds.length > 0) onDropProperty(slot, propertyIds);
            }}
        >
            <span className="slot-top">
                <span className="slot-num">{String(index + 1).padStart(2, '0')}</span>
                <span className="slot-state">Disponible</span>
            </span>
            <span className="slot-center">
                <span className="plus">
                    <Icon icon="pcom-icon-circle-plus-outline" size={18} />
                </span>
                <span className="label">
                    {hasSelection ? `Asignar aquí (${selectedCount})` : 'Asignar aquí'}
                </span>
                <span className="slot-id">Slot {slot.id}</span>
            </span>
            <span className="vence">
                <Icon icon="pcom-icon-calendar-date" size={12} />
                <span>
                    {niceDate ? `Vence ${niceDate}` : 'Sin vigencia'}
                    {days != null && days >= 0 ? ` · ${days}d` : ''}
                </span>
            </span>
        </AvailableCard>
    );
};

const SlotCategorySection = ({
    category,
    slots,
    pageSize,
    pageSizeOptions,
    onPageSizeChange,
    propertyById,
    selectedOccupiedIds,
    canAssignByClick,
    selectedCount,
    onToggleOccupied,
    onSelectAllOccupied,
    onLiberateSingle,
    onClearOccupied,
    onAssignClick,
    onDropProperty,
    onReplaceProperty,
}: SlotCategorySectionProps) => {
    const { occupied, available } = partitionSlots(slots);
    const summary = summarizeSlots(slots);
    const meta = CATEGORY_META[category];
    const ratio = summary.total > 0 ? summary.occupied / summary.total : 0;

    // El selector del panel controla cuántos slots se muestran por página; el
    // mismo tamaño aplica a ocupados y disponibles.
    const effectivePageSize = pageSize && pageSize > 0 ? pageSize : DEFAULT_SECTION_PAGE_SIZE;

    const occupiedIds = occupied.map((slot) => slot.id);
    const selectedInCategory = selectedOccupiedIds.filter((id) => occupiedIds.includes(id));
    const allSelected = occupiedIds.length > 0 && selectedInCategory.length === occupiedIds.length;

    // Paginación independiente para ocupados y disponibles.
    const [occupiedPage, setOccupiedPage] = useState(1);
    const [availablePage, setAvailablePage] = useState(1);

    const occupiedPageCount = Math.max(1, Math.ceil(occupied.length / effectivePageSize));
    const availablePageCount = Math.max(1, Math.ceil(available.length / effectivePageSize));
    const safeOccupiedPage = Math.min(occupiedPage, occupiedPageCount);
    const safeAvailablePage = Math.min(availablePage, availablePageCount);

    useEffect(() => {
        if (occupiedPage !== safeOccupiedPage) setOccupiedPage(safeOccupiedPage);
    }, [occupiedPage, safeOccupiedPage]);
    useEffect(() => {
        if (availablePage !== safeAvailablePage) setAvailablePage(safeAvailablePage);
    }, [availablePage, safeAvailablePage]);

    const occupiedStart = (safeOccupiedPage - 1) * effectivePageSize;
    const availableStart = (safeAvailablePage - 1) * effectivePageSize;
    const occupiedPageItems = occupied.slice(occupiedStart, occupiedStart + effectivePageSize);
    const availablePageItems = available.slice(availableStart, availableStart + effectivePageSize);

    return (
        <CategoryCard as="section" $category={category} aria-label={`Espacios ${meta.title}`}>
            <CategoryHeader>
                <CategoryHeading>
                    <CategoryIcon $category={category}>
                        {category === 'prime' ? (
                            <span className="crown-glyph" aria-hidden>
                                ♔
                            </span>
                        ) : (
                            <Icon icon={meta.icon} size={18} />
                        )}
                    </CategoryIcon>
                    <CategoryTitle $category={category}>
                        {meta.title}
                        <span>
                            {summary.occupied} ocupados · {summary.available} disponibles
                            {summary.expiringSoon > 0 ? ` · ${summary.expiringSoon} por vencer` : ''}
                        </span>
                    </CategoryTitle>
                </CategoryHeading>
            </CategoryHeader>

            <ProgressTrack>
                <ProgressFill $category={category} $ratio={ratio} />
            </ProgressTrack>

            <GroupLabel>
                {occupiedIds.length > 0 ? (
                    <OccupiedHeaderLeft>
                        <SelectAllRow as="label" style={{ margin: 0, textTransform: 'none' }}>
                            <input
                                type="checkbox"
                                data-id={`select-all-occupied-${category}`}
                                checked={allSelected}
                                onChange={(e) =>
                                    onSelectAllOccupied(occupiedIds, e.target.checked)
                                }
                            />
                            Seleccionar todos los ocupados ({summary.occupied})
                        </SelectAllRow>
                        {selectedInCategory.length > 0 && (
                            <ClearLink
                                type="button"
                                data-id={`clear-occupied-${category}`}
                                onClick={onClearOccupied}
                            >
                                <Icon icon="pcom-icon-close" size={12} /> Limpiar selección
                            </ClearLink>
                        )}
                    </OccupiedHeaderLeft>
                ) : (
                    <span />
                )}
                <span>Ocupados ({summary.occupied})</span>
            </GroupLabel>
            {occupied.length === 0 ? (
                <EmptyGroup>No hay slots ocupados en esta categoría.</EmptyGroup>
            ) : (
                <>
                    <SlotGrid $cols={SLOT_COLUMNS}>
                        {occupiedPageItems.map((slot, index) => (
                            <OccupiedSlotCardView
                                key={slot.id}
                                slot={slot}
                                index={occupiedStart + index}
                                category={category}
                                property={
                                    slot.property_id != null
                                        ? propertyById[slot.property_id]
                                        : undefined
                                }
                                selected={selectedOccupiedIds.includes(slot.id)}
                                onToggle={onToggleOccupied}
                                onLiberate={onLiberateSingle}
                                onReplaceProperty={onReplaceProperty}
                            />
                        ))}
                    </SlotGrid>
                    <Pagination
                        dataId={`occupied-pagination-${category}`}
                        page={safeOccupiedPage}
                        total={occupied.length}
                        pageSize={effectivePageSize}
                        onPageChange={setOccupiedPage}
                    />
                </>
            )}

            <GroupLabel>
                <span>Disponibles ({summary.available})</span>
                {!canAssignByClick && available.length > 0 && (
                    <span style={{ textTransform: 'none', fontWeight: 500 }}>
                        Selecciona o arrastra una propiedad libre
                    </span>
                )}
            </GroupLabel>
            {available.length === 0 ? (
                <EmptyGroup>No hay slots disponibles en esta categoría.</EmptyGroup>
            ) : (
                <>
                    <SlotGrid $cols={SLOT_COLUMNS}>
                        {availablePageItems.map((slot, index) => (
                            <AvailableSlotCardView
                                key={slot.id}
                                slot={slot}
                                index={availableStart + index}
                                category={category}
                                selectedCount={selectedCount}
                                onAssignClick={onAssignClick}
                                onDropProperty={onDropProperty}
                            />
                        ))}
                    </SlotGrid>
                    <Pagination
                        dataId={`available-pagination-${category}`}
                        page={safeAvailablePage}
                        total={available.length}
                        pageSize={effectivePageSize}
                        onPageChange={setAvailablePage}
                        pageSizeOptions={pageSizeOptions}
                        onPageSizeChange={onPageSizeChange}
                    />
                </>
            )}
        </CategoryCard>
    );
};

export default SlotCategorySection;
